import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Send, Loader2, MessageCircle, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface ChatCitation {
  id: string;
  title: string;
  description: string;
  slug: string;
  url: string;
  similarity: number;
  citationNumber: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: ChatCitation[];
  additionalContext?: string | null;
  contextSource?: 'lovable' | 'magisterium' | null;
  contextModel?: string | null;
  isLoadingContext?: boolean;
}

interface LibraryChatCardProps {
  title?: string;
  description?: string;
  className?: string;
}

const LibraryChatCard: React.FC<LibraryChatCardProps> = ({
  title = "Chat With the Repository",
  description = "Ask questions and get answers sourced from the Repository.",
  className
}) => {
  const { organization } = useOrganization();
  const [chatQuestion, setChatQuestion] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<'searching' | 'generating' | 'context' | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to newest message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [chatMessages, isChatLoading]);

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatQuestion.trim() || isChatLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: chatQuestion.trim()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    const question = chatQuestion.trim();
    setChatQuestion("");
    setIsChatLoading(true);
    setLoadingStage('searching');

    try {
      setLoadingStage('generating');
      
      // Fire both requests in parallel for faster loading
      const mainPromise = supabase.functions.invoke('library-chat', {
        body: { 
          question,
          organization_id: organization?.id
        }
      });

      const contextPromise = supabase.functions.invoke('library-chat-context', {
        body: { 
          question,
          organization_id: organization?.id,
          context_provider: 'auto'
        }
      });

      // Wait for main response first
      const { data, error } = await mainPromise;

      if (error) throw error;

      // Handle response
      const answerContent = typeof data.answer === 'object' 
        ? data.answer.fromLibrary 
        : data.answer;
      
      const messageId = crypto.randomUUID();
      const hasMoreContext = data.answer?.hasMoreContext && data.contextProvider !== 'none';
      
      const assistantMessage: ChatMessage = {
        id: messageId,
        role: 'assistant',
        content: answerContent || "Unable to generate an answer.",
        citations: data.citations || [],
        isLoadingContext: hasMoreContext
      };
      
      setChatMessages(prev => [...prev, assistantMessage]);
      setIsChatLoading(false);
      setLoadingStage(null);

      // Check if context is ready (it's been loading in parallel!)
      if (hasMoreContext) {
        setLoadingStage('context');
        try {
          // Context request already started, just await its result
          const { data: contextData } = await contextPromise;

          if (contextData?.additionalContext) {
            setChatMessages(prev => prev.map(msg => 
              msg.id === messageId 
                ? { 
                    ...msg, 
                    additionalContext: contextData.additionalContext,
                    contextSource: contextData.contextSource,
                    contextModel: contextData.contextModel,
                    isLoadingContext: false
                  }
                : msg
            ));
          } else {
            // No additional context, just clear loading state
            setChatMessages(prev => prev.map(msg => 
              msg.id === messageId ? { ...msg, isLoadingContext: false } : msg
            ));
          }
        } catch (contextErr) {
          console.error('Context loading error:', contextErr);
          setChatMessages(prev => prev.map(msg => 
            msg.id === messageId ? { ...msg, isLoadingContext: false } : msg
          ));
        }
        setLoadingStage(null);
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: err.message?.includes('429') || err.status === 429
          ? "Rate limit exceeded. Please try again later."
          : err.message?.includes('402') || err.status === 402
          ? "AI usage limit reached."
          : "Failed to get an answer. Please try again."
      };
      setChatMessages(prev => [...prev, errorMessage]);
      toast.error("Failed to get an answer");
      setIsChatLoading(false);
      setLoadingStage(null);
    }
  };

  const getContextSourceLabel = (source: string | null | undefined, model: string | null | undefined) => {
    if (source === 'magisterium') return 'From Magisterium AI';
    if (model) return `From AI (${model})`;
    return 'From AI';
  };

  return (
    <Card className={`flex flex-col h-[600px] ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle size={18} className="text-primary" />
          <h3 className="font-semibold">{title}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {/* Chat Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {chatMessages.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Ask a question to get started</p>
            </div>
          )}
          
          {chatMessages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div className="space-y-3">
                    {/* Main answer from library */}
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    
                    {/* Citations */}
                    {msg.citations && msg.citations.length > 0 && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Sources from Repository:
                        </p>
                        <div className="space-y-1">
                          {msg.citations.map((citation, idx) => (
                            <Link
                              key={citation.id}
                              to={`/library/${citation.slug}`}
                              className="flex items-center gap-2 text-xs text-primary hover:underline"
                            >
                              <span className="font-medium">[{idx + 1}]</span>
                              <span className="truncate">{citation.title}</span>
                              <ExternalLink size={10} />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Additional context loading */}
                    {msg.isLoadingContext && (
                      <div className="pt-2 border-t border-border/50">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Loading additional context...</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Additional context from Magisterium/AI */}
                    {msg.additionalContext && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          {getContextSourceLabel(msg.contextSource, msg.contextModel)}:
                        </p>
                        <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground">
                          <ReactMarkdown>{msg.additionalContext}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isChatLoading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 size={14} className="animate-spin" />
                  <span>
                    {loadingStage === 'searching' && 'Searching repository...'}
                    {loadingStage === 'generating' && 'Generating answer...'}
                    {loadingStage === 'context' && 'Loading additional context...'}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={chatEndRef} />
        </div>
      </ScrollArea>

      {/* Input Form */}
      <form onSubmit={handleChatSubmit} className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            value={chatQuestion}
            onChange={(e) => setChatQuestion(e.target.value)}
            placeholder="Ask a question..."
            disabled={isChatLoading}
            className="flex-1"
          />
          <Button type="submit" disabled={isChatLoading || !chatQuestion.trim()} size="icon">
            {isChatLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default LibraryChatCard;
